import React, {
  useState, useContext, useEffect, useRef,
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Grid, Stack, Box, Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Content from './components/Content';
import { PlanProvider, PlanContext } from './context/PlanContext';
import Preamble from './components/Preamble';
import InvestmentSnapshot from './components/PlanningSteps/InvestmentSnapshot';
import PostPlanSubmission from './components/PlanningSteps/PostPlanSubmission';
import hasCompletedInvestment from './helpers/hasCompletedInvestment';

export { steps, FundType, TierType } from './constants';

const PlanningContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [nextDisabled, setNextDisabled] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [showPreamble, setShowPreamble] = useState(true);
  const [showInvestmentSnapshot, setShowInvestmentSnapshot] = useState(false);
  const [planComplete, setPlanComplete] = useState(false);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(true);
  const hasInitializedPortfolio = useRef(false);
  const {
    userPortfolio,
    planningConfig,
    loading,
    isError,
    plan,
    updatedPlan,
  } = useContext(PlanContext);

  const planHasUnsavedChanges = JSON.stringify(plan) !== JSON.stringify(updatedPlan);

  useEffect(() => {
    if (
      !hasInitializedPortfolio.current
      && userPortfolio
      && Object.keys(userPortfolio).length > 0
    ) {
      hasInitializedPortfolio.current = true;
      if (userPortfolio.investmentPlanPreambleCompletedDate) {
        setShowPreamble(false);
      }
      if (userPortfolio.investmentPlanCompletionDate) {
        setPlanComplete(true);
      }
      setShowInvestmentSnapshot(hasCompletedInvestment(userPortfolio));
      setIsPortfolioLoading(false);
    }
  }, [userPortfolio]);

  const handleStepClick = (index) => {
    const isForwardNavigation = index > currentStep;
    if (
      index <= furthestStep
      && (!isForwardNavigation || (!nextDisabled && !planHasUnsavedChanges))
    ) {
      setNextDisabled(true);
      setCurrentStep(index);
    }
  };

  const handlePreambleComplete = () => {
    setShowPreamble(false);
  };

  const handleCreateNewPlan = () => {
    setNextDisabled(true);
    setCurrentStep(0);
    setFurthestStep(0);
    setShowInvestmentSnapshot(hasCompletedInvestment(userPortfolio));
    setPlanComplete(false);
  };

  if (isError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="body1">
          We could not load your venture plan. Please try again later.
        </Typography>
      </Box>
    );
  }

  if (
    isPortfolioLoading
    || Object.keys(planningConfig).length === 0
    || loading
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  if (planComplete) {
    return <PostPlanSubmission onCreateNewPlan={handleCreateNewPlan} />;
  }

  if (showPreamble) {
    return <Preamble onComplete={handlePreambleComplete} />;
  }

  if (showInvestmentSnapshot) {
    return (
      <InvestmentSnapshot
        onContinue={() => setShowInvestmentSnapshot(false)}
      />
    );
  }

  return (
    <Grid container>
      {!isMobile && (
        <Grid item sm={3}>
          <Sidebar
            currentStep={currentStep}
            furthestStep={furthestStep}
            canNavigateForward={!nextDisabled && !planHasUnsavedChanges}
            onStepClick={handleStepClick}
          />
        </Grid>
      )}
      <Grid item xs={isMobile ? 12 : 9}>
        <Stack>
          {isMobile && <Header />}
          <Content
            currentStep={currentStep}
            setNextDisabled={setNextDisabled}
          />
          <Footer
            isMobile={isMobile}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            setFurthestStep={setFurthestStep}
            isNextDisabled={nextDisabled}
            setNextDisabled={setNextDisabled}
            onComplete={() => setPlanComplete(true)}
            onReturnToInvestmentSnapshot={
              hasCompletedInvestment(userPortfolio)
                ? () => setShowInvestmentSnapshot(true)
                : null
            }
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

const Planning = () => (
  <PlanProvider>
    <Helmet>
      <title>Planning - Alumni Ventures</title>
      <link rel="shortcut icon" sizes="16x16" />
    </Helmet>
    <PlanningContent />
  </PlanProvider>
);

const mapStateToProps = (state) => ({
  dispatch: state.dispatch,
});

export default withRouter(connect(mapStateToProps)(Planning));
